import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ArticleListItem from "@/components/base/ArticleListItem";
import Pagination from "@/components/base/Pagination";
import type { Category, SortKey } from "@/lib/news";
import { getArticlesByCategory, paginate, sortArticles } from "@/lib/news";

const PAGE_SIZE = 15;

interface CategoryListProps {
  category: Category;
}

const sortOptions: { key: SortKey; label: string; icon: string }[] = [
  { key: "time", label: "最新发布", icon: "ri-time-line" },
  { key: "heat", label: "最热内容", icon: "ri-fire-line" },
];

export default function CategoryList({ category }: CategoryListProps) {
  const [sortKey, setSortKey] = useState<SortKey>("time");
  const [page, setPage] = useState(1);

  const all = useMemo(() => getArticlesByCategory(category.id), [category.id]);
  const sorted = useMemo(() => sortArticles(all, sortKey), [all, sortKey]);
  const { items, total, totalPages, currentPage } = paginate(sorted, page, PAGE_SIZE);

  const handleSort = (key: SortKey) => {
    setSortKey(key);
    setPage(1);
  };

  const handlePage = (next: number) => {
    setPage(next);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-6 md:py-10">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 px-1 py-1">
          {sortOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => handleSort(option.key)}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-label transition-colors cursor-pointer ${
                sortKey === option.key
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-primary-600"
              }`}
            >
              <i className={option.icon} />
              {option.label}
            </button>
          ))}
        </div>
        <p className="text-sm text-foreground-500">
          共 <span className="font-medium text-foreground-800">{total}</span> 条内容
        </p>
      </div>

      {items.length > 0 ? (
        <div className="space-y-3">
          {items.map((article, index) => (
            <ArticleListItem key={article.id} article={article} order={index} />
          ))}
        </div>
      ) : (
        <EmptyState
          onReset={() => {
            setSortKey("time");
            setPage(1);
          }}
        />
      )}

      {items.length > 0 && (
        <div className="mt-8">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onChange={handlePage}
          />
        </div>
      )}
    </div>
  );
}

function EmptyState({ onReset }: { onReset: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
        <i className="ri-inbox-line text-2xl" />
      </span>
      <p className="mt-4 font-heading text-base font-semibold text-foreground-800">
        暂无相关黑料信息
      </p>
      <p className="mt-2 text-sm text-foreground-500">
        当前分类下暂时没有内容，您可以重置筛选或返回首页。
      </p>
      <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={onReset}
          className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-label text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
        >
          重置筛选
        </button>
        <Link
          to="/"
          className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    </div>
  );
}